import { Check, ClipboardCopy, Loader2, X } from "lucide-react";
import { useState } from "react";
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui";
import type { ProactiveLogDetail, ProactiveLogItem } from "@/types";
import { compactTime } from "@/utils/format";
import { cn } from "@/lib/utils";
import {
  kindLabel,
  outcomeDetail,
  outcomeDisplay,
  proactiveCaseText,
} from "@/utils/engagement";

function TimelineStep({
  label,
  at,
  derived,
}: {
  label: string;
  at: string | null | undefined;
  derived?: boolean;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-[110px_1fr] items-center gap-3 border-b border-border py-2 last:border-b-0",
        !at && "text-muted-foreground",
      )}
    >
      <span className="text-sm">{label}</span>
      <span className="flex items-center gap-2 text-sm">
        {at ? compactTime(at) : "-"}
        {at && derived && (
          <Badge size="compact" title="직접 관측이 아니라 파생 신호에서 추정한 값입니다.">
            추정
          </Badge>
        )}
      </span>
    </div>
  );
}

function SettingCard({ detail }: { detail: ProactiveLogDetail }) {
  const setting = detail.recipient_setting;
  if (!setting) return null;
  return (
    <Card className="grid gap-1.5 p-3.5 text-sm">
      <div className="font-medium">수신자 발송 설정</div>
      <div className="text-muted-foreground">
        선제 메시지 {setting.proactive_message_enabled ? "ON" : "OFF"} · 일일 상한{" "}
        {setting.proactive_daily_max}건 · 음소거 세션 {setting.muted_session_count}개
      </div>
      {/* The sender's COALESCE prefers the setting row; saying which one was
          used stops a stale legacy toggle from being read as the live value. */}
      {setting.source === "legacy_users_column" && (
        <div className="text-xs text-muted-foreground">
          설정 행이 없어 레거시 users 컬럼 + 기본값으로 표시했습니다.
        </div>
      )}
    </Card>
  );
}

export function ProactiveLogModal({
  item,
  detail,
  loading,
  error,
  open,
  onClose,
}: {
  item?: ProactiveLogItem;
  detail?: ProactiveLogDetail;
  loading: boolean;
  error: string;
  open: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  if (!item) return null;

  const outcome = outcomeDisplay(item.outcome);
  const opened = item.signals.opened_at;
  const source = detail ?? item;

  const copyCase = async () => {
    await navigator.clipboard.writeText(proactiveCaseText(source));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="grid max-h-[calc(100vh-56px)] w-[min(1000px,calc(100vw-56px))] max-w-none grid-rows-[auto_1fr] gap-0 overflow-hidden rounded-[10px] border border-border bg-card p-0 text-[length:inherit] text-foreground shadow-modal ring-0 sm:max-w-none"
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-5.5 py-4.5">
          <div className="min-w-0">
            <DialogTitle className="mb-1 text-[22px] font-bold">
              {item.session.display_name || "선제 메시지"}
            </DialogTitle>
            <DialogDescription className="text-[length:inherit] text-muted-foreground">
              {item.recipient_email || "수신자 미상"} · {kindLabel(item.kind)} ·{" "}
              {item._environment} · {compactTime(item.created_at)}
            </DialogDescription>
            <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
              message: {item.message_id}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* The improvement path: prompts live in server code, so handing a
                concrete case to the dev team is what an operator can act on. */}
            <Button variant="outline" onClick={copyCase}>
              {copied ? <Check /> : <ClipboardCopy />}
              {copied ? "복사됨" : "사례 복사"}
            </Button>
            <DialogClose render={<Button variant="outline" size="icon" aria-label="닫기" title="닫기" />}>
              <X />
            </DialogClose>
          </div>
        </div>

        <div className="min-w-0 overflow-x-hidden overflow-y-auto px-5.5 pt-4.5 pb-6">
          {error && <Banner tone="error">{error}</Banner>}

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Badge>{outcome.label}</Badge>
            {outcomeDetail(item) && (
              <span className="text-sm text-muted-foreground">{outcomeDetail(item)}</span>
            )}
            {item.requested_by && (
              <Badge size="compact">요청자 {item.requested_by}</Badge>
            )}
          </div>
          <p className="mb-4 text-sm text-muted-foreground">{outcome.hint}</p>

          <Card className="mb-4 p-4">
            <div className="mb-2 text-sm font-medium">보낸 내용</div>
            <p className="whitespace-pre-wrap">{item.content || "(본문 없음)"}</p>
          </Card>

          <div className="mb-4 grid gap-4 md:grid-cols-[1.4fr_1fr]">
            <Card className="p-4">
              <div className="mb-2 text-sm font-medium">반응 타임라인</div>
              <TimelineStep label="발송" at={item.created_at} />
              <TimelineStep label="전달" at={item.signals.delivered_at} />
              <TimelineStep
                label="열람"
                at={opened?.value}
                derived={opened?.source === "derived"}
              />
              <TimelineStep label="알림 닫음" at={item.signals.dismissed_at} />
              <TimelineStep label="답장" at={item.signals.replied_at} />
              <TimelineStep label="다른 세션 복귀" at={item.signals.next_activity_at} />
            </Card>
            {detail && <SettingCard detail={detail} />}
          </div>

          {loading && (
            <p className="mb-4">
              <Loader2 className="mr-1.5 inline-block size-4 animate-spin align-[-3px]" />
              전후 대화를 불러오는 중...
            </p>
          )}

          {detail && detail.history.length > 0 && (
            <Card className="p-4">
              <div className="mb-2 text-sm font-medium">전후 대화</div>
              {/* Failed turns are hidden here on purpose: this renders what the
                  user could actually see, unlike the log row above. */}
              <div className="grid gap-2">
                {detail.history.map((message) => (
                  <div
                    key={message.chat_message_id}
                    className={cn(
                      "rounded-lg border border-border p-2.5 text-sm",
                      message.chat_message_id === item.message_id &&
                        "border-primary bg-primary/8",
                    )}
                  >
                    <div className="mb-1 text-xs text-muted-foreground">
                      {message.role}
                      {message.sender_email ? ` · ${message.sender_email}` : ""} ·{" "}
                      {compactTime(message.created_at)}
                    </div>
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
